import { request } from './request';
import { queueNotice } from '../components/Notice';
import {
  map,
  of,
  switchMap,
  catchError,
  shareReplay,
  Observable,
  EMPTY,
  merge,
  throwError,
} from 'rxjs';
import { convertToAsyncTaskState } from '../utils/rxjs/task';
import { bind, shareLatest } from '@react-rxjs/core';
import { driver$ } from './driver';
import { createSignal } from '@react-rxjs/utils';

export type LockStatus = 'LOCKED' | 'UNLOCKED' | 'UNKNOWN' | null;
export type CommandState = 'SENT' | 'FAILED' | null;
export interface LockState {
  commandState: CommandState;
  errorMessage: string | null;
}

export type Vehicle = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  inputs: Record<string, string>;
  firstDriver: {
    fullName: string;
  };
  lockStatus?: LockStatus;
  lockState?: LockState;
  athState: 0 | 1 | null;
};

export type ObjectSearch = {
  count: number;
  items: Vehicle[];
};

export type VehicleList = { count: number; items: Vehicle[] };

export const objectById = (id: number) =>
  request<ObjectSearch>(
    `/gateway/tracking-service/v20160301/objects?id=${id}&view=MINI`
  );

export const unasignVehicleReq = (id: number) =>
  request<''>(
    `/gateway/lcv-driver-service/v20220128/driver-assignation/unassign?objectId=${id}`,
    {
      method: 'POST',
    }
  );

export const assignVehicleReq = (id: number) =>
  request<void>(
    `/gateway/lcv-driver-service/v20220128/driver-assignation/assign?objectId=${id}`,
    {
      method: 'POST',
    }
  );

export const getVehicles = () =>
  request<VehicleList>(
    `/gateway/lcv-driver-service/v20220128/driver-assignation/available-objects`
  );

export const [unasignVehicleSignal$, unasignVehicle] = createSignal<number>();

export const unasignVehicle$ = unasignVehicleSignal$.pipe(
  switchMap((id) =>
    unasignVehicleReq(id).pipe(
      catchError(() => {
        queueNotice({
          message: 'Vehicle.Unassign.Error',
        });
        return EMPTY;
      }),
      map(() => null)
    )
  ),
  shareLatest()
);

export const vehicleList$: Observable<VehicleList> = getVehicles().pipe(
  catchError((e) => {
    queueNotice({
      message: 'Vehicle.Get.Error',
    });
    return throwError(() => e);
  })
);

export const [useVehicleList] = bind(
  vehicleList$.pipe(convertToAsyncTaskState()),
  {
    result: null,
    running: false,
    error: null,
  }
);

export const [assignVehicleSignal$, assignVehicle] = createSignal<Vehicle>();

export const assignVehicle$ = assignVehicleSignal$.pipe(
  switchMap((vehicle) =>
    assignVehicleReq(vehicle.id).pipe(
      catchError(() => {
        queueNotice({ message: 'Vehicle.Assign.Error' });
        return EMPTY;
      }),
      map(() => vehicle)
    )
  ),
  shareLatest()
);

export const vehicle$ = merge(
  driver$.pipe(map((driver) => driver?.assignation?.objectId ?? null)),
  assignVehicle$.pipe(map((vehicle) => vehicle?.id ?? null)),
  unasignVehicle$
).pipe(
  switchMap((objectId) => {
    if (!objectId) return of(null);
    return objectById(objectId).pipe(
      map((search) => (search.count > 0 ? search.items[0] : null)),
      catchError(() => {
        queueNotice({
          message: 'Vehicle.Assign.Info.Error',
        });
        return of(null);
      })
    );
  }),
  shareReplay({ refCount: true, bufferSize: 1 })
);

export const [useVehicle] = bind(vehicle$.pipe(convertToAsyncTaskState()));
